import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
} from 'platform/components';
import {Grid, GridItem, Heading, HStack, Right, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {indexBy, isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty, isNumber} from 'ramda-adjunct';

import {
  useGetRentalVehicleQuery,
  useListRentalPriceGroupQuery,
  usePutRentalVehicleMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  handleApiError,
  refreshDmsDatagrid,
  useVatRatesOptions,
} from '@dms/shared';

import {TestIdProps, useNavigate} from 'shared/web';

import {PriceGroupTable, FormValues} from './PriceGroupTable';

interface ChangePriceGroupFormProps extends TestIdProps {
  vehicleId: string;
}

const schema = z.object({
  priceGroupId: z.string(),
  priceGroupItemId: z.string(),
});

export function ChangePriceGroupForm(props: ChangePriceGroupFormProps) {
  const navigate = useNavigate();

  const {
    data: currentRentalVehicle,
    isLoading: isLoadingRentalVehicle,
    isError: isErrorRentalVehicle,
  } = useGetRentalVehicleQuery({vehicleId: props.vehicleId});

  const {
    data: rentalPriceGroups,
    isLoading: isLoadingRentalPriceGroups,
    isError: isErrorRentalPriceGroups,
  } = useListRentalPriceGroupQuery({});

  const isLoading = isLoadingRentalVehicle || isLoadingRentalPriceGroups;
  const isError = isErrorRentalVehicle || isErrorRentalPriceGroups;

  const [putRentalVehicle] = usePutRentalVehicleMutation();

  const [vatRates] = useVatRatesOptions();
  const vatRatesByValue = indexBy((item) => item.value, vatRates);

  const priceGroupOptions =
    rentalPriceGroups?.map((priceGroup) => ({
      value: priceGroup.rentalPriceGroupId,
      label: priceGroup.name,
      fieldLabel: priceGroup.vatType,
    })) ?? [];

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    if (isNil(currentRentalVehicle)) {
      throw new Error('Rental vehicle not found');
    }

    const updatedRentalVehicle = {
      carParkId: currentRentalVehicle.carParkId,
      availability: currentRentalVehicle.availability,
      note: currentRentalVehicle.note,
      equipment: currentRentalVehicle.equipment,
      rentalPriceGroup: {
        rentalPriceGroupId: values.priceGroupId ?? '',
        rentalPriceGroupItemId: values.priceGroupItemId ?? '',
      },
    };

    await putRentalVehicle({
      vehicleId: props.vehicleId,
      rentalVehicleRequestBody: updatedRentalVehicle,
    })
      .unwrap()
      .then(() => {
        refreshDmsDatagrid('rental-vehicle-price-log');
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      experimentalZodSchema={schema}
      defaultValues={{
        priceGroupId: currentRentalVehicle?.rentalPriceGroup?.rentalPriceGroupId,
        priceGroupItemId: currentRentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId,
      }}
    >
      {(control, formApi) => {
        const selectedPriceGroupId = formApi.watch('priceGroupId');

        const handlePriceGroupChange = (value: string | number | null) => {
          if (isNil(value) || isNumber(value)) {
            formApi.setValue('priceGroupId', null);
            formApi.setValue('priceGroupItemId', null);
            return;
          }
          formApi.setValue('priceGroupId', value);
          formApi.setValue('priceGroupItemId', null);
        };

        const selectedPriceGroup = rentalPriceGroups?.find(
          (group) => group.rentalPriceGroupId === selectedPriceGroupId
        );

        return (
          <VStack spacing={4}>
            <DataStatus isLoading={isLoading} isError={isError}>
              <Grid columns={4} align="center">
                <VStack>
                  <FormField
                    control={control}
                    name="priceGroupId"
                    type="choice"
                    menuInPortal
                    label={i18n.t('entity.rental.labels.name')}
                    options={priceGroupOptions}
                    formatOptionLabel={(option, meta) => {
                      const vatLabel = vatRatesByValue[option.fieldLabel ?? '']?.label;

                      if (meta.context === 'value') {
                        return option.label;
                      }

                      return (
                        <HStack spacing={2} align="baseline">
                          <Text size="small">{option.label}</Text>

                          <Show when={isNotNilOrEmpty(vatLabel)}>
                            <Text color="secondary" size="xSmall">
                              {vatLabel}
                            </Text>
                          </Show>
                        </HStack>
                      );
                    }}
                    onChange={handlePriceGroupChange}
                  />
                </VStack>

                <VStack>
                  <Label>{i18n.t('general.labels.vat')}</Label>
                  <Heading size={4} alternative>
                    {isNotNil(selectedPriceGroup)
                      ? (vatRatesByValue[selectedPriceGroup.vatType]?.label ?? EMPTY_PLACEHOLDER)
                      : EMPTY_PLACEHOLDER}
                  </Heading>
                </VStack>

                <GridItem span={2}>
                  <Right>
                    <Button
                      title={i18n.t('general.labels.settings')}
                      onClick={() => navigate(settingsRoutes.priceGroupList)}
                      variant="link"
                      leftIcon="action/settings"
                    />
                  </Right>
                </GridItem>
              </Grid>

              <PriceGroupTable control={control} formApi={formApi} />
            </DataStatus>

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.cancel')}
                variant="secondary"
                onClick={closeCurrentDialog}
              />
              <FormButton
                title={i18n.t('general.actions.saveChanges')}
                variant="primary"
                type="submit"
                control={control}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
